import React, { useEffect, useState } from 'react';
import { ui } from '../ui';
import {
  WeeklyMetrics, RosterMember,
  getTimeTrackingWeeklyMetrics, getTimeTrackingRoster,
} from '../api';

// Staffing-reduction gates from the CS Cost Reduction handoff, hours/week:
// baseline 478 (Jan–Jun 2026 average), Milestone 1 ≤ 430 (from Aug 3, 2026),
// Dec 2026 target ≈ 335. The Workforce tables are loaded by the CS project's
// Python loaders; this tab is a read-only view over them.
const BASELINE_HOURS = 478;
const MILESTONE1_HOURS = 430;
const DEC_TARGET_HOURS = 335;

const fmtWeek = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const fmtHours = (h: number) => h.toFixed(2);
const fmtPct = (p: number) => `${p > 0 ? '+' : ''}${(p * 100).toFixed(1)}%`;

// Worked-hours cell tone: green once Milestone 1 is met, amber below baseline, red above.
const workedTone = (h: number) =>
  h <= MILESTONE1_HOURS ? 'text-green-600' : h <= BASELINE_HOURS ? 'text-amber-600' : 'text-red-600';

export const TimeTrackingAdmin: React.FC = () => {
  const [weeks, setWeeks] = useState<WeeklyMetrics[]>([]);
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = () =>
    Promise.all([getTimeTrackingWeeklyMetrics(), getTimeTrackingRoster()])
      .then(([w, r]) => { setWeeks(w); setRoster(r); });

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  const activeRoster = roster.filter(r => r.IsActive);

  return (
    <div className="space-y-6">
      {/* ── Weekly hours ─────────────────────────────────────── */}
      <section className={ui.sectionCard}>
        <div className="mb-1 flex items-center justify-between gap-3">
          <h2 className={ui.sectionTitle}>Weekly Hours</h2>
          <button className={ui.btnSecondary} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <p className="mb-4 text-sm text-muted">
          Sunday-start weeks from MakeShift schedules and ADP timecards. Targets (worked hrs/wk):{' '}
          baseline <span className="font-medium text-secondary">{BASELINE_HOURS}</span> ·{' '}
          Milestone 1 <span className="font-medium text-green-600">≤ {MILESTONE1_HOURS}</span> ·{' '}
          Dec 2026 <span className="font-medium text-secondary">≈ {DEC_TARGET_HOURS}</span>.
        </p>
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>Week (Sun–Sat)</th>
                <th className={`${ui.th} text-right`}>Scheduled</th>
                <th className={`${ui.th} text-right`}>Worked</th>
                <th className={`${ui.th} text-right`}>Answering-role</th>
                <th className={`${ui.th} text-right`}>Recommended</th>
                <th className={`${ui.th} text-right`}>Worked FTE</th>
                <th className={`${ui.th} text-right`}>vs Baseline</th>
              </tr>
            </thead>
            <tbody className={ui.tbody}>
              {weeks.map(w => (
                <tr key={w.WeekStartDate} className="hover:bg-surface-hover">
                  <td className={`${ui.td} whitespace-nowrap`}>{fmtWeek(w.WeekStartDate)} – {fmtWeek(w.WeekEndDate)}</td>
                  <td className={`${ui.td} text-right tabular-nums`}>{fmtHours(w.ScheduledHours)}</td>
                  <td className={`${ui.td} text-right tabular-nums font-semibold ${workedTone(w.WorkedHours)}`}>
                    {fmtHours(w.WorkedHours)}
                  </td>
                  <td className={`${ui.td} text-right tabular-nums`}>{fmtHours(w.AnsweringRoleWorkedHours)}</td>
                  <td className={`${ui.td} text-right tabular-nums`}>{fmtHours(w.RecommendedHours)}</td>
                  <td className={`${ui.td} text-right tabular-nums`}>{w.WorkedFTE.toFixed(2)}</td>
                  <td className={`${ui.td} text-right tabular-nums ${w.WorkedVsBaselinePct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {fmtPct(w.WorkedVsBaselinePct)}
                  </td>
                </tr>
              ))}
              {!loading && weeks.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                    No weekly data yet — the Workforce tables are populated by the CS project loaders.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Roster ───────────────────────────────────────────── */}
      <section className={ui.sectionCard}>
        <h2 className={`${ui.sectionTitle} mb-1`}>Roster</h2>
        <p className="mb-4 text-sm text-muted">
          {activeRoster.length > 0
            ? `${activeRoster.length} active agent${activeRoster.length === 1 ? '' : 's'}. `
            : ''}
          Read-only — the roster is seeded and maintained by the CS project.
        </p>
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>Name</th>
                <th className={ui.th}>Role</th>
                <th className={ui.th}>Time Zone</th>
                <th className={ui.th}>Active</th>
              </tr>
            </thead>
            <tbody className={ui.tbody}>
              {roster.map(r => (
                <tr key={r.RosterID} className="hover:bg-surface-hover">
                  <td className={ui.td}>{r.CanonicalName}</td>
                  <td className={ui.td}>{r.Role}</td>
                  <td className={ui.td}>{r.TimeZone}</td>
                  <td className={ui.td}>{r.IsActive ? 'Yes' : <span className="text-muted">No</span>}</td>
                </tr>
              ))}
              {!loading && roster.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted">
                    No roster loaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
